import { computed, makeObservable } from 'mobx'
import { Cubit } from '../core'
import type { MeetingCubit } from '../MeetingCubit'
import {
  InteractiveElementPromptVersion,
  RoomStatePromptVersion,
  ToolPromptVersion,
} from '../../models/PromptVersion'
import { CurrentPromptVersion } from '../../models/CurrentPromptVersion'
import { getPromptVersions } from '../../firestore/PromptVersion'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import { InteractiveElementPromptType } from '../../firestore/PromptInteractiveElementType/types'
import type { StaticModelCollection } from '../../firestore-mobx/model'
import {
  PromptType,
  PromptVersionStatus,
} from '../../firestore/PromptVersion/types'
import { getCurrentPromptVersions } from '../../firestore/CurrentPromptVersion'
import { RoomStatePromptType } from '../../firestore/PromptRoomStateType/types'
import { SlideQuestionType } from '../../models/SlideQuestionType'
import {
  roomStateUpdatePumpSlide,
  updateRoomStateWithPumpSlide,
} from '../../firestore/RoomState'
import { serverTimestamp } from 'firebase/firestore'
import { ToolPromptType } from '../../firestore/PromptToolType/types'
import type { PromptPair } from '../../firestore/PromptVersion/types'
import { BackendConfigModel } from '../BackendConfigModel'

export type { PromptPair }
export { RoomStatePromptType, ToolPromptType, InteractiveElementPromptType }

export class DevModeController extends Cubit {
  meeting: MeetingCubit
  repository: FirebaseRepository
  moderationConfig: BackendConfigModel

  // DEV MODE
  soloModeratorBotPromptVersions: StaticModelCollection<InteractiveElementPromptVersion>
  soloGraderBotPromptVersions: StaticModelCollection<InteractiveElementPromptVersion>
  soloRolePlayBotPromptVersions: StaticModelCollection<InteractiveElementPromptVersion>
  soloTutorBotPromptVersions: StaticModelCollection<InteractiveElementPromptVersion>
  soloFormCompletionBotPromptVersions: StaticModelCollection<InteractiveElementPromptVersion>

  soloModeratorBotCurrentPromptVersions: StaticModelCollection<CurrentPromptVersion>
  soloGraderBotCurrentPromptVersions: StaticModelCollection<CurrentPromptVersion>
  soloRolePlayBotCurrentPromptVersions: StaticModelCollection<CurrentPromptVersion>
  soloTutorBotCurrentPromptVersions: StaticModelCollection<CurrentPromptVersion>
  soloFormCompletionBotCurrentPromptVersions: StaticModelCollection<CurrentPromptVersion>

  // room state - moderator bot (on room entry)
  roomStateModeratorBotPromptVersions: StaticModelCollection<RoomStatePromptVersion>
  roomStateModeratorBotCurrentPromptVersions: StaticModelCollection<CurrentPromptVersion>

  // tools
  toolTaskCompletedVersions: StaticModelCollection<ToolPromptVersion>
  toolTalkingOffTopicVersions: StaticModelCollection<ToolPromptVersion>

  toolTaskCompletedCurrentVersions: StaticModelCollection<CurrentPromptVersion>
  toolTalkingOffTopicCurrentVersions: StaticModelCollection<CurrentPromptVersion>

  interactiveElementVersions = new Map<
    InteractiveElementPromptType,
    {
      promptVersions: StaticModelCollection<InteractiveElementPromptVersion>
      currentVersions: StaticModelCollection<CurrentPromptVersion>
    }
  >()

  aiAgentPromptTypes = [
    InteractiveElementPromptType.mg1000OutlineDiscussionCleanup,
    InteractiveElementPromptType.mg1000OutlineDiscussionConceptual,
    InteractiveElementPromptType.mg1000OutlineDiscussionRoleplay,
    InteractiveElementPromptType.mg1000OutlineDiscussionScenario,
    InteractiveElementPromptType.mg1000OutlineDiscussionTransmission,
    InteractiveElementPromptType.mg1000OutlineRefreshSegment,
    InteractiveElementPromptType.mg1000OutlineRefreshSlide,
    InteractiveElementPromptType.mg1000OutlineRefreshExhibit,
    InteractiveElementPromptType.mg1000PreviewGenerator,
  ]

  constructor(
    meeting: MeetingCubit,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    options: Record<string, never> = {}
  ) {
    super()
    this.meeting = meeting
    this.repository = meeting.repository
    const repository = this.repository

    makeObservable(this)

    this.moderationConfig = new BackendConfigModel(
      this.repository,
      'moderation_config'
    )

    // used only in dev mode
    this.soloModeratorBotPromptVersions =
      InteractiveElementPromptVersion.emptyCollection(repository)
    this.soloGraderBotPromptVersions =
      InteractiveElementPromptVersion.emptyCollection(repository)
    this.soloRolePlayBotPromptVersions =
      InteractiveElementPromptVersion.emptyCollection(repository)
    this.soloTutorBotPromptVersions =
      InteractiveElementPromptVersion.emptyCollection(repository)
    this.soloFormCompletionBotPromptVersions =
      InteractiveElementPromptVersion.emptyCollection(repository)

    this.soloModeratorBotCurrentPromptVersions =
      CurrentPromptVersion.emptyCollection(repository)
    this.soloGraderBotCurrentPromptVersions =
      CurrentPromptVersion.emptyCollection(repository)
    this.soloRolePlayBotCurrentPromptVersions =
      CurrentPromptVersion.emptyCollection(repository)
    this.soloTutorBotCurrentPromptVersions =
      CurrentPromptVersion.emptyCollection(repository)
    this.soloFormCompletionBotCurrentPromptVersions =
      CurrentPromptVersion.emptyCollection(repository)

    this.roomStateModeratorBotPromptVersions =
      RoomStatePromptVersion.emptyCollection(repository)
    this.roomStateModeratorBotCurrentPromptVersions =
      CurrentPromptVersion.emptyCollection(repository)

    this.toolTaskCompletedVersions =
      ToolPromptVersion.emptyCollection(repository)
    this.toolTaskCompletedCurrentVersions =
      CurrentPromptVersion.emptyCollection(repository)

    this.toolTalkingOffTopicVersions =
      ToolPromptVersion.emptyCollection(repository)
    this.toolTalkingOffTopicCurrentVersions =
      CurrentPromptVersion.emptyCollection(repository)

    this.toolTalkingOffTopicVersions =
      ToolPromptVersion.emptyCollection(repository)
    this.toolTalkingOffTopicCurrentVersions =
      CurrentPromptVersion.emptyCollection(repository)

    for (const promptType of this.aiAgentPromptTypes) {
      this.interactiveElementVersions.set(promptType, {
        promptVersions:
          InteractiveElementPromptVersion.emptyCollection(repository),
        currentVersions: CurrentPromptVersion.emptyCollection(repository),
      })
    }
  }

  initialize(): void {
    // extra guard
    if (!this.meeting.isDevMode) return

    this.devModeStartPromptVersionStreams()
    this.moderationConfig.fetchIfNotFetched()
  }

  devModeStartPromptVersionStreams() {
    this.addStream(
      getPromptVersions(this.repository, {
        promptType: PromptType.interactiveElement,
        subType: InteractiveElementPromptType.soloGraderBot,
      }),
      (prompts) => {
        this.soloGraderBotPromptVersions.replaceModels(prompts)
      }
    )
    this.addStream(
      getPromptVersions(this.repository, {
        promptType: PromptType.interactiveElement,
        subType: InteractiveElementPromptType.soloRolePlayBot,
      }),
      (prompts) => {
        this.soloRolePlayBotPromptVersions.replaceModels(prompts)
      }
    )
    this.addStream(
      getPromptVersions(this.repository, {
        promptType: PromptType.interactiveElement,
        subType: InteractiveElementPromptType.soloModeratorBot,
      }),
      (prompts) => {
        this.soloModeratorBotPromptVersions.replaceModels(prompts)
      }
    )
    this.addStream(
      getPromptVersions(this.repository, {
        promptType: PromptType.interactiveElement,
        subType: InteractiveElementPromptType.soloTutorBot,
      }),
      (prompts) => {
        this.soloTutorBotPromptVersions.replaceModels(prompts)
      }
    )
    this.addStream(
      getPromptVersions(this.repository, {
        promptType: PromptType.interactiveElement,
        subType: InteractiveElementPromptType.soloFormCompletionBot,
      }),
      (versions) => {
        this.soloFormCompletionBotPromptVersions.replaceModels(versions)
      }
    )

    this.addStream(
      getCurrentPromptVersions(this.repository, {
        promptType: PromptType.interactiveElement,
        subType: InteractiveElementPromptType.soloGraderBot,
      }),
      (versions) => {
        this.soloGraderBotCurrentPromptVersions.replaceModels(versions)
      }
    )
    this.addStream(
      getCurrentPromptVersions(this.repository, {
        promptType: PromptType.interactiveElement,
        subType: InteractiveElementPromptType.soloRolePlayBot,
      }),
      (versions) => {
        this.soloRolePlayBotCurrentPromptVersions.replaceModels(versions)
      }
    )
    this.addStream(
      getCurrentPromptVersions(this.repository, {
        promptType: PromptType.interactiveElement,
        subType: InteractiveElementPromptType.soloModeratorBot,
      }),
      (versions) => {
        this.soloModeratorBotCurrentPromptVersions.replaceModels(versions)
      }
    )
    this.addStream(
      getCurrentPromptVersions(this.repository, {
        promptType: PromptType.interactiveElement,
        subType: InteractiveElementPromptType.soloTutorBot,
      }),
      (versions) => {
        this.soloTutorBotCurrentPromptVersions.replaceModels(versions)
      }
    )
    this.addStream(
      getCurrentPromptVersions(this.repository, {
        promptType: PromptType.interactiveElement,
        subType: InteractiveElementPromptType.soloFormCompletionBot,
      }),
      (versions) => {
        this.soloFormCompletionBotCurrentPromptVersions.replaceModels(versions)
      }
    )

    // Room State Moderator Bot
    this.addStream(
      getPromptVersions(this.repository, {
        promptType: PromptType.roomState,
        subType: RoomStatePromptType.moderatorBot,
      }),
      (prompts) => {
        this.roomStateModeratorBotPromptVersions.replaceModels(prompts)
      }
    )

    this.addStream(
      getCurrentPromptVersions(this.repository, {
        promptType: PromptType.roomState,
        subType: RoomStatePromptType.moderatorBot,
      }),
      (versions) => {
        this.roomStateModeratorBotCurrentPromptVersions.replaceModels(versions)
      }
    )

    // Tool Prompt Task Completed
    this.addStream(
      getPromptVersions(this.repository, {
        promptType: PromptType.tool,
        subType: ToolPromptType.moderatorBotModerationTaskCompleted,
      }),
      (prompts) => {
        this.toolTaskCompletedVersions.replaceModels(prompts)
      }
    )

    this.addStream(
      getCurrentPromptVersions(this.repository, {
        promptType: PromptType.tool,
        subType: ToolPromptType.moderatorBotModerationTaskCompleted,
      }),
      (versions) => {
        this.toolTaskCompletedCurrentVersions.replaceModels(versions)
      }
    )

    // Tool Prompt Talking Off Topic
    this.addStream(
      getPromptVersions(this.repository, {
        promptType: PromptType.tool,
        subType: ToolPromptType.moderatorBotUserTalkingOffTopic,
      }),
      (prompts) => {
        this.toolTalkingOffTopicVersions.replaceModels(prompts)
      }
    )

    this.addStream(
      getCurrentPromptVersions(this.repository, {
        promptType: PromptType.tool,
        subType: ToolPromptType.moderatorBotUserTalkingOffTopic,
      }),
      (versions) => {
        this.toolTalkingOffTopicCurrentVersions.replaceModels(versions)
      }
    )

    for (const promptType of this.aiAgentPromptTypes) {
      const store = this.interactiveElementVersions.get(promptType)
      if (store) {
        this.addStream(
          getPromptVersions(this.repository, {
            promptType: PromptType.interactiveElement,
            subType: promptType,
          }),
          (prompts) => {
            store.promptVersions.replaceModels(prompts)
          }
        )
        this.addStream(
          getCurrentPromptVersions(this.repository, {
            promptType: PromptType.interactiveElement,
            subType: promptType,
          }),
          (prompts) => store.currentVersions.replaceModels(prompts)
        )
      }
    }
  }

  @computed
  get showModerationPrompts() {
    // 1. moderationBotEnabled is set in moderation_config
    // 2. slideDeck has moderation tasks in questions

    if (this.moderationConfig.data.moderatorBotEnabled !== true) return false

    return this.meeting.questions.some(
      (q) => q.data.questionType === SlideQuestionType.moderationTask
    )
  }

  @computed
  get showAiAgentPrompts() {
    return this.meeting.questions.some(
      (q) => q.data.questionType === SlideQuestionType.aiAgent
    )
  }

  @computed
  get usedAiAgentPromptTypes(): InteractiveElementPromptType[] {
    return (
      this.meeting.questions
        // narrow to ai agent questions only
        .filter((q) => q.data.questionType === SlideQuestionType.aiAgent)
        // filter out questions without promptTypeId or with promptTypeId not in keys
        .filter((q) => Boolean(q.data.promptTypeId))
        // narrow to keys only, cast to InteractiveElementPromptType so we can filter in next step
        // we can bang here because of the filter above
        .map((q) => q.data.promptTypeId! as InteractiveElementPromptType)
        // filter to valid prompt types only
        .filter((type) => this.aiAgentPromptTypes.includes(type))
        // sort by the order in aiAgentPromptTypes
        .sort(
          (a, b) =>
            this.aiAgentPromptTypes.indexOf(a) -
            this.aiAgentPromptTypes.indexOf(b)
        )
    )
  }

  getSelectedPromptVersionId(pair: PromptPair) {
    const overrideValue = this.getPromptVersionOverrideForPair(pair)
    if (overrideValue) return overrideValue

    return this.getCurrentPromptVersionId(pair)
  }

  getCurrentPromptVersionId(pair: PromptPair) {
    const versions = this.getCurrentPromptVersionsModels(pair)
    if (!versions || versions.length === 0) return undefined

    return versions[0].data.promptVersionId
  }

  getPromptVersions(pair: PromptPair) {
    const currentVersionId = this.getCurrentPromptVersionId(pair)
    const models = this.getPromptVersionsModels(pair)
    if (!models) return []

    const currentVersions = this.getCurrentPromptVersionsModels(pair) || []
    const previouslyInstalledVersions = new Set(models.map(({ id }) => id))

    // remove archived versions
    return models
      .filter((version) => {
        const installed =
          currentVersions.length &&
          currentVersions[0].data.promptVersionId === version.id
        const status = (() => {
          if (installed) return PromptVersionStatus.published

          if (
            version.data.versionStatus === PromptVersionStatus.published &&
            previouslyInstalledVersions.has(version.id)
          )
            return PromptVersionStatus.archived

          return version.data.versionStatus
        })()

        return status !== PromptVersionStatus.archived
      })
      .sort((a, b) => {
        // installed goes first
        if (a.id === currentVersionId) return -1
        if (b.id === currentVersionId) return 1
        return a.data.versionName.localeCompare(b.data.versionName)
      })
  }

  getPromptVersionsModels(pair: PromptPair) {
    if (pair.promptType === PromptType.interactiveElement) {
      if (this.interactiveElementVersions.has(pair.subType)) {
        const store = this.interactiveElementVersions.get(pair.subType)
        return store?.promptVersions.models
      }
      switch (pair.subType) {
        case InteractiveElementPromptType.soloModeratorBot:
          return this.soloModeratorBotPromptVersions.models
        case InteractiveElementPromptType.soloGraderBot:
          return this.soloGraderBotPromptVersions.models
        case InteractiveElementPromptType.soloRolePlayBot:
          return this.soloRolePlayBotPromptVersions.models
        case InteractiveElementPromptType.soloTutorBot:
          return this.soloTutorBotPromptVersions.models
        case InteractiveElementPromptType.soloFormCompletionBot:
          return this.soloFormCompletionBotPromptVersions.models
      }
    } else if (pair.promptType === PromptType.roomState) {
      switch (pair.subType) {
        case RoomStatePromptType.moderatorBot:
          return this.roomStateModeratorBotPromptVersions.models
      }
    } else if (pair.promptType === PromptType.tool) {
      switch (pair.subType) {
        case ToolPromptType.moderatorBotModerationTaskCompleted:
          return this.toolTaskCompletedVersions.models

        case ToolPromptType.moderatorBotUserTalkingOffTopic:
          return this.toolTalkingOffTopicVersions.models
      }
    }
  }

  getCurrentPromptVersionsModels(pair: PromptPair) {
    if (pair.promptType === PromptType.interactiveElement) {
      if (this.interactiveElementVersions.has(pair.subType)) {
        const store = this.interactiveElementVersions.get(pair.subType)
        return store?.currentVersions.models
      }
      switch (pair.subType) {
        case InteractiveElementPromptType.soloModeratorBot:
          return this.soloModeratorBotCurrentPromptVersions.models
        case InteractiveElementPromptType.soloGraderBot:
          return this.soloGraderBotCurrentPromptVersions.models
        case InteractiveElementPromptType.soloRolePlayBot:
          return this.soloRolePlayBotCurrentPromptVersions.models
        case InteractiveElementPromptType.soloTutorBot:
          return this.soloTutorBotCurrentPromptVersions.models
        case InteractiveElementPromptType.soloFormCompletionBot:
          return this.soloTutorBotCurrentPromptVersions.models
      }
    } else if (pair.promptType === PromptType.roomState) {
      switch (pair.subType) {
        case RoomStatePromptType.moderatorBot:
          return this.roomStateModeratorBotCurrentPromptVersions.models
      }
    } else if (pair.promptType === PromptType.tool) {
      switch (pair.subType) {
        case ToolPromptType.moderatorBotModerationTaskCompleted:
          return this.toolTaskCompletedCurrentVersions.models
        case ToolPromptType.moderatorBotUserTalkingOffTopic:
          return this.toolTalkingOffTopicCurrentVersions.models
      }
    }
  }

  getPromptVersionOverrideForPair(pair: PromptPair): string | undefined {
    const key = this.buildKey(pair)
    if (!key) return undefined

    return this.meeting.roomState.data.promptVersionOverride?.[key]
  }

  async updatePromptVersionOverride(pair: PromptPair, value?: string) {
    const key = this.buildKey(pair)
    if (!key) return

    const defaultId = this.getCurrentPromptVersionId(pair)
    const meeting = this.meeting

    const currentOverrides = meeting.roomState.data.promptVersionOverride ?? {}

    const promptVersionOverride = { ...currentOverrides }

    if (!value || value === defaultId) {
      delete promptVersionOverride[key]
    } else {
      promptVersionOverride[key] = value
    }

    await updateRoomStateWithPumpSlide(meeting.firestore, meeting.roomId, {
      promptVersionOverride,
      updatedAt: serverTimestamp(),
    })
  }

  buildKey(pair: PromptPair): string | undefined {
    return `${pair.promptType}|${pair.subType}`
  }

  @computed
  get devModeQuestionType(): SlideQuestionType | null {
    const slideQuestions = this.meeting.questions.filter(
      (q) => q.data.slideId === this.meeting.currentSlideId
    )

    const questionType = slideQuestions.find(
      (q) =>
        q.questionType === SlideQuestionType.moderatorBot ||
        q.questionType === SlideQuestionType.graderBot ||
        q.questionType === SlideQuestionType.tutorBot ||
        q.questionType === SlideQuestionType.rolePlayBot ||
        q.questionType === SlideQuestionType.formCompletionBot
    )?.questionType

    return questionType ?? null
  }

  @computed
  get isDevMode(): boolean {
    return this.meeting.isDevMode
  }

  @computed
  get devModeSlideIndex(): number | undefined {
    if (!this.isDevMode || !this.meeting.devModeSlideId) return undefined

    const slideIndex = this.meeting.slides.findIndex(
      (slide) => slide.id === this.meeting.devModeSlideId
    )

    return slideIndex >= 0 ? slideIndex : undefined
  }

  _devModePumpSlideDebounce: NodeJS.Timeout | null = null
  devModePumpSlide() {
    // only run this in dev mode
    if (!this.isDevMode) return

    if (this._devModePumpSlideDebounce) {
      clearTimeout(this._devModePumpSlideDebounce)
    }
    // eslint-disable-next-line no-console
    console.log('devModePumpSlide - debouncing')
    this._devModePumpSlideDebounce = setTimeout(async () => {
      // eslint-disable-next-line no-console
      console.log('devModePumpSlide - running')
      await roomStateUpdatePumpSlide(this.repository.firestore, {
        roomId: this.meeting.roomId,
      })
      this._devModePumpSlideDebounce = null
    }, 1000)
  }

  async devModeSetSlide(slideIndex: number) {
    if (!this.isDevMode) throw new Error('Callable in dev mode only')

    this.meeting.setSlide(slideIndex)
  }

  get isInDevModeOrCorreDemo() {
    return (
      this.isDevMode ||
      (this.meeting.isDemo && this.repository.breakoutUser?.isCorre)
    )
  }

  async devModeMakeSolo(userId?: string) {
    if (!this.isInDevModeOrCorreDemo)
      throw new Error('Callable in dev mode or demo only')

    const userIdToUse = userId || this.repository.uid
    const allUserIds = this.meeting.allUserIds
    const uniqueUserIds = Array.from(new Set(allUserIds))
    const allUserIdsWithoutSelf = uniqueUserIds.filter(
      (id) => id !== userIdToUse
    )
    return updateRoomStateWithPumpSlide(
      this.repository.firestore,
      this.meeting.roomId,
      {
        userIds: [userIdToUse],
        hiddenUserIds: allUserIdsWithoutSelf,
      }
    )
  }

  // In Non-solo, we add the same user id twice - it will be ignored by the UI,
  // but the backend will treat it as a non-solo mode
  async devModeMakeNonSolo(userId?: string) {
    if (!this.isInDevModeOrCorreDemo)
      throw new Error('Callable in dev mode or demo only')

    const userIdToUse = userId || this.repository.uid
    const allUserIds = this.meeting.allUserIds
    const uniqueUserIds = Array.from(new Set(allUserIds))

    if (uniqueUserIds.length === 1) {
      return updateRoomStateWithPumpSlide(
        this.meeting.firestore,
        this.meeting.roomId,
        {
          userIds: [userIdToUse, userIdToUse],
          hiddenUserIds: [],
        }
      )
    }

    return updateRoomStateWithPumpSlide(
      this.meeting.firestore,
      this.meeting.roomId,
      {
        userIds: allUserIds,
        hiddenUserIds: [],
      }
    )
  }

  async devModeRestore() {
    if (!this.isDevMode) throw new Error('Callable in dev mode only')

    const userIds = this.meeting.roomState.data.userIds
    const hiddenUserIds = this.meeting.roomState.data.hiddenUserIds || []
    const allUserIds = Array.from(
      new Set([...userIds, ...hiddenUserIds, this.repository.uid])
    )
    await updateRoomStateWithPumpSlide(
      this.repository.firestore,
      this.meeting.roomId,
      {
        userIds: allUserIds,
        hiddenUserIds: [],
      }
    )
  }
}
